import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Accreditation1 from './AccreditedInvestor/Accreditation1'
import AcknowledgementPage from './AcknowledgementPage'
import Email from './Email'
import InvestingPerson from './InvestingPerson'
import JointlyOptions from './InvestingPerson/JointlyOptions'
import RetirementAccntOptions from './InvestingPerson/RetirementAccntOptions'
import InvestmentAmount from './InvestmentAmount'
import InvestorPersonalInfo from './InvestorPersonalInfo'
import Payments from './Payments'
import PaymentSelection from './PaymentSelection'
import SelfAccreditingInvestor from './SelfAccreditingInvestor'
import SocialSecurityNum from './SocialSecurityNum'
import SuccessInvestment from './SuccessInvestment'
import FinancialStanding from './UnAccreditedInvestor/FinancialStanding'

import { notification } from 'antd'
import { useRiseUpAPIV1 } from 'hooks/useRiseUpAPIV1'
import { consoleLog } from 'utils/simpleLogger'
import { accreditedPages, IPage, unAccreditedPages } from '../../constants/pages'

const GetStarted = () => {
  const [formState, setFormState] = useState({
    step: '1',
  })
  const [investorType, setInvestorType] = useState<string>('')
  const [progressState, setProgressState] = useState(0)
  const [subscriptionAgreement, setSubscriptionAgreement] = useState(false)
  const [currentStage, setCurrentStage] = useState(0)
  const [retirementAccntType, setRetirementAccntType] = useState('')
  const { getIPAddress } = useRiseUpAPIV1()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    resetField,
    getValues,
    control,
    setError,
    trigger,
    reset,
    unregister,
    formState: { errors, isValid },
    clearErrors,
  } = useForm({ mode: 'onSubmit' })

  const [paymentOptionSelected, setPaymentOptionSelected] = useState<string | null>(
    getValues('investingProfile.paymentMethod'),
  )

  const validateFields = (data: []) => {
    trigger(data)
  }

  const onSubmit = (data: any) => {
    consoleLog('Form data submitted', data)
  }

  const nextStep = (page: string) => {
    calcProgressState(page)
    setFormState({ step: page })
  }

  const previousStep = (page: string) => {
    calcProgressState(page)
    setFormState({ step: page })
  }

  const calcProgressState = (pageId: string) => {
    if (getValues('investingProfile.accreditedInvestor') === 'yes') {
      const index = accreditedPages.findIndex((page: IPage) => page.pageId === pageId)
      setProgressState(((index + 1) / accreditedPages.length) * 100)
    } else {
      const index = unAccreditedPages.findIndex((page: IPage) => page.pageId === pageId)
      setProgressState(((index + 1) / unAccreditedPages.length) * 100)
    }
  }

  const fetchUserIpAddress = async () => {
    const ipAddress = await getIPAddress()
    setValue('ipAddress', ipAddress)
  }

  // destroy warning notif on every step change
  useEffect(() => {
    fetchUserIpAddress()
    notification.destroy('form-notif')
    clearErrors()
    window.scrollTo(0, 0)
  }, [formState])

  const renderFormStep = () => {
    switch (formState.step) {
      case '1':
        return (
          <Email
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            getValues={getValues}
            setValue={setValue}
            setFormState={setFormState}
            calcProgressState={calcProgressState}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
            validateFields={validateFields}
            clearErrors={clearErrors}
            reset={reset}
          />
        )
      case '2':
        return (
          <InvestingPerson
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            resetField={resetField}
            setValue={setValue}
            getValues={getValues}
            investorType={investorType}
            setInvestorType={setInvestorType}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
            retirementAccntType={retirementAccntType}
            setRetirementAccntType={setRetirementAccntType}
            unregister={unregister}
          />
        )
      case '2a':
        return (
          <JointlyOptions
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            control={control}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            investorType={investorType}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
          />
        )
      case '2b':
        return (
          <RetirementAccntOptions
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            control={control}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            investorType={investorType}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
            retirementAccntType={retirementAccntType}
            setRetirementAccntType={setRetirementAccntType}
          />
        )
      case '3':
        return (
          <InvestorPersonalInfo
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            control={control}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            investorType={investorType}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
            validateFields={validateFields}
            clearErrors={clearErrors}
          />
        )
      case '4':
        return (
          <SocialSecurityNum
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            control={control}
            watch={watch}
            resetField={resetField}
            getValues={getValues}
            setValue={setValue}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
            setError={setError}
            validateFields={validateFields}
            clearErrors={clearErrors}
          />
        )
      case '5':
        return (
          <InvestmentAmount
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            watch={watch}
            control={control}
            getValues={getValues}
            setValue={setValue}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
            validateFields={validateFields}
            clearErrors={clearErrors}
          />
        )
      case '6':
        return (
          <SelfAccreditingInvestor
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
          />
        )
      case '6A-1':
        return (
          <Accreditation1
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            getValues={getValues}
            setValue={setValue}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
          />
        )
      case '6B-1':
        return (
          <FinancialStanding
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            watch={watch}
            control={control}
            getValues={getValues}
            setValue={setValue}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
            validateFields={validateFields}
            clearErrors={clearErrors}
          />
        )

      case '7':
        return (
          <AcknowledgementPage
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            getValues={getValues}
            setValue={setValue}
            subscriptionAgreement={subscriptionAgreement}
            setSubscriptionAgreement={setSubscriptionAgreement}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
          />
        )
      case '8':
        return (
          <PaymentSelection
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            setPaymentOptionSelected={setPaymentOptionSelected}
            getValues={getValues}
            setValue={setValue}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
          />
        )
      case '9':
        return (
          <Payments
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            paymentOptionSelected={paymentOptionSelected}
            watch={watch}
            control={control}
            getValues={getValues}
            setValue={setValue}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
          />
        )

      case 'Success':
        return <SuccessInvestment getValues={getValues} reset={reset} />
      default:
        return (
          <Email
            registerField={register}
            handleSubmit={onSubmit}
            errors={errors}
            isValid={isValid}
            useForm={useForm}
            nextStep={nextStep}
            previousStep={previousStep}
            getValues={getValues}
            setValue={setValue}
            setFormState={setFormState}
            calcProgressState={calcProgressState}
            currentStage={currentStage}
            setCurrentStage={setCurrentStage}
            validateFields={validateFields}
            clearErrors={clearErrors}
            reset={reset}
          />
        )
    }
  }

  return (
    <div className="font-inter">
      {/* <div className="w-full bg-gray-200 rounded-full h-10 lg:h-3.5 dark:bg-gray-700">
        <div className="bg-blue-500 h-10 lg:h-3.5 rounded-full" style={{ width: `${progressState}%` }}></div>
      </div> */}

      <form onSubmit={handleSubmit(onSubmit)}>
        {renderFormStep()}
        {/* {renderBtn()} */}
      </form>
      {/* {process.env.NODE_ENV === 'development' ? <pre>{JSON.stringify(watch(), null, 2)}</pre> : null} */}
    </div>
  )
}

export default GetStarted
